import React, { useMemo, useState } from "react";
import type { Ticket } from "../types";

type QuickPreset = {
    key: string;
    label: string;
    description: string;
    defaultTitle: string;
    defaultStartTime: string;
    defaultEndTime: string;
};

const PRESETS: QuickPreset[] = [
    {
        key: "lunch",
        label: "Frokost",
        description: "Standard frokostbilletter",
        defaultTitle: "Frokost billet",
        defaultStartTime: "11:00",
        defaultEndTime: "13:00",
    },
    {
        key: "dinner",
        label: "Aftensmad",
        description: "Standard aftensmadsbilletter",
        defaultTitle: "Aftenmad billet",
        defaultStartTime: "17:00",
        defaultEndTime: "20:00",
    },
    {
        key: "custom",
        label: "Custom",
        description: "Lav dine egne billetter",
        defaultTitle: "",
        defaultStartTime: "11:00",
        defaultEndTime: "13:00",
    },
];

function formatDateInput(d: Date) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function combineDateAndTime(date: string, time: string) {
    return `${date}T${time}:00`;
}

function eachDayInclusive(startDate: string, endDate: string) {
    const out: string[] = [];
    const current = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);

    while (current <= end) {
        out.push(formatDateInput(current));
        current.setDate(current.getDate() + 1);
    }

    return out;
}

export default function TicketQuickCreatePanel({
        onBulkCreate,
        eventId,
    }: {
        onBulkCreate: (tickets: Ticket[]) => Promise<void> | void;
        eventId: number;
    }) {
    const [presetKey, setPresetKey] = useState("lunch");
    const preset = useMemo(
        () => PRESETS.find((p) => p.key === presetKey) ?? PRESETS[0],
        [presetKey]
    );

    const today = formatDateInput(new Date());

    const [title, setTitle] = useState(preset.defaultTitle);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [startTime, setStartTime] = useState(preset.defaultStartTime);
    const [endTime, setEndTime] = useState(preset.defaultEndTime);
    const [active, setActive] = useState(true);

    React.useEffect(() => {
        setTitle(preset.defaultTitle);
        setStartTime(preset.defaultStartTime);
        setEndTime(preset.defaultEndTime);
    }, [presetKey]);

    const previewCount = useMemo(() => {
        if (!startDate || !endDate) return 0;
        return eachDayInclusive(startDate, endDate).length;
    }, [startDate, endDate]);

    const handleCreate = async () => {
        if (!title.trim()) {
            alert("Skriv en titel");
            return;
        }

        if (!startDate || !endDate || !startTime || !endTime) {
            alert("Udfyld dato og tid");
            return;
        }

        if (endDate < startDate) {
            alert("Slutdato m� ikke v�re f�r startdato");
            return;
        }

        const dates = eachDayInclusive(startDate, endDate);

        const tickets: Ticket[] = dates.map((date) => ({
            eventID: eventId,
            ticketDescription: title.trim(),
            ticketTimeStart: combineDateAndTime(date, startTime),
            ticketTimeEnd: combineDateAndTime(date, endTime),
            active,
        }));

        await onBulkCreate(tickets);
    };

    return (
        <div
            style={{
                marginTop: 12,
                padding: 12,
                border: "1px solid #ddd",
                borderRadius: 10,
                background: "#fafafa",
            }}
        >
            <div style={{ fontWeight: 700, marginBottom: 10 }}>Hurtig opret billetter</div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {PRESETS.map((p) => (
                    <button
                        key={p.key}
                        type="button"
                        className="edit-btn"
                        onClick={() => setPresetKey(p.key)}
                        style={{ opacity: presetKey === p.key ? 1 : 0.7 }}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            <div style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>
                {preset.description}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Titel</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Fx Frokost billet"
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc" }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Fra dato</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc" }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Til dato</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc" }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Start tid</label>
                    <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc" }}
                    />
                </div>

                <div>
                    <label style={{ fontSize: 12, fontWeight: 700 }}>Slut tid</label>
                    <input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid #ccc" }}
                    />
                </div>
            </div>

            <div style={{ marginTop: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                        type="checkbox"
                        checked={active}
                        onChange={(e) => setActive(e.target.checked)}
                    />
                    <span>Aktive billetter</span>
                </label>
            </div>

            <div
                style={{
                    marginTop: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                }}
            >
                <div style={{ fontSize: 13, color: "#555" }}>
                    Der oprettes <strong>{previewCount}</strong> billet{previewCount === 1 ? "" : "ter"}
                </div>

                <button className="add-btnnew" type="button" onClick={handleCreate}>
                    Opret mange
                </button>
            </div>
        </div>
    );
}